import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Phone, Mail, CheckCircle, XCircle, Truck, Stethoscope } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const DistributorManagement = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'distributor', 'vet_doctor'

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/distributors-and-vets`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
      toast.error('Failed to fetch users');
    }
  };

  const handleDelete = async (userId, role) => {
    const roleLabel = role === 'vet_doctor' ? 'vet doctor' : 'distributor';
    if (!window.confirm(`Are you sure you want to delete this ${roleLabel}?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeader());
      toast.success(`${roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1)} deleted successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${roleLabel}`);
    }
  };

  const handleApprove = async (userId, name) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, {}, getAuthHeader());
      toast.success(`${name} has been approved`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRevoke = async (userId, name) => {
    if (!window.confirm(`Are you sure you want to revoke approval for ${name}?`)) return;
    try {
      await axios.put(`${API}/users/${userId}/revoke`, {}, getAuthHeader());
      toast.success(`Approval revoked for ${name}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke approval');
    }
  };

  const filteredUsers = filter === 'all' 
    ? users 
    : users.filter(u => u.role === filter);

  const distributorCount = users.filter(u => u.role === 'distributor').length;
  const vetDoctorCount = users.filter(u => u.role === 'vet_doctor').length;
  const pendingCount = users.filter(u => u.is_approved === false).length;
  const approvedCount = users.filter(u => u.is_approved !== false).length;

  return (
    <div className="space-y-6" data-testid="distributor-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Distributor/Vet Doctor Management
          </h1>
          <p className="text-lg text-slate-600 mt-2">Manage and approve registered distributors and vet doctors</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 bg-gradient-to-br from-purple-50 to-white border-l-4 border-purple-500">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-purple-600" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Distributors</p>
          </div>
          <p className="text-3xl font-bold text-slate-900 mt-2">{distributorCount}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
          <div className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-blue-600" />
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Vet Doctors</p>
          </div>
          <p className="text-3xl font-bold text-slate-900 mt-2">{vetDoctorCount}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pending Approval</p>
          <p className="text-3xl font-bold text-amber-600 mt-2">{pendingCount}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Approved</p>
          <p className="text-3xl font-bold text-emerald-600 mt-2">{approvedCount}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all' 
              ? 'bg-slate-900 text-white' 
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All ({users.length})
        </button>
        <button
          onClick={() => setFilter('distributor')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            filter === 'distributor' 
              ? 'bg-purple-600 text-white' 
              : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
          }`}
        >
          <Truck className="w-4 h-4" />
          Distributors ({distributorCount})
        </button>
        <button
          onClick={() => setFilter('vet_doctor')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            filter === 'vet_doctor' 
              ? 'bg-blue-600 text-white' 
              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
          }`}
        >
          <Stethoscope className="w-4 h-4" />
          Vet Doctors ({vetDoctorCount})
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="distributors-table-body">
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="font-medium">{user.name}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      user.role === 'vet_doctor' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {user.role === 'vet_doctor' ? (
                        <><Stethoscope className="w-3 h-3" /> Vet Doctor</>
                      ) : (
                        <><Truck className="w-3 h-3" /> Distributor</>
                      )}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {user.email}
                    </div>
                  </td>
                  <td>
                    {user.phone ? (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {user.phone}
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td>
                    {user.is_approved === false ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        Pending
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Approved
                      </span>
                    )}
                  </td>
                  <td className="text-slate-500 text-sm">{user.created_at ? formatDate(user.created_at.split('T')[0]) : '-'}</td>
                  <td>
                    <div className="flex gap-1">
                      {user.is_approved === false ? (
                        <button
                          data-testid={`approve-user-${user.id}`}
                          onClick={() => handleApprove(user.id, user.name)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Approve"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          data-testid={`revoke-user-${user.id}`}
                          onClick={() => handleRevoke(user.id, user.name)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title="Revoke Approval"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        data-testid={`delete-user-${user.id}`}
                        onClick={() => handleDelete(user.id, user.role)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              {filter === 'all' 
                ? 'No distributors or vet doctors registered yet.'
                : filter === 'distributor'
                  ? 'No distributors registered yet.'
                  : 'No vet doctors registered yet.'}
            </div>
          )}
        </div>
      </div>

      <div className="card p-4 bg-blue-50 border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Distributors and Vet Doctors register themselves through the login page by selecting their role. 
          They must be approved before they can access the system.
        </p>
      </div>
    </div>
  );
};

export default DistributorManagement;
